import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { CustomPeriodPickerOptions, GeneralComponentClass } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import FormRangeCategoryBaseComponent from 'webapp/components/form/range/category-base';

import type DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type Granulation from 'webapp/models-custom/granulation';

export type Item = DateRange | DsCompare | Granulation;

export interface Category {
	id: number | string;
	title: string;
	isDatepicker: boolean;
	isSingleItem: boolean;
	items: Item[];
}

export interface Args {
	onSelectItem: (item: Item, skipClose?: boolean) => void;
	onContentChange: () => void;
	showAllTime?: boolean;
	hideUnsupported?: boolean;
	customPeriodPickerOptions?: CustomPeriodPickerOptions;
	theme?: string;
}

export default abstract class FormListBaseComponent<T extends Args> extends Component<T> {
	@tracked openCategoryId: number | string | null = null;

	@tracked selectedItem: Item | null = null;

	abstract get shownCategories(): Category[];

	abstract componentClass: GeneralComponentClass;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setFirstSelected();
	}

	get theme() {
		return this.args.theme;
	}

	setFirstSelected() {
		// possibly extended
	}

	@action
	toggleCategory(categoryId: number | string) {
		if (this.openCategoryId === categoryId) {
			this.openCategoryId = null;
		} else {
			this.openCategoryId = categoryId;
		}

		schedule('afterRender', () => {
			this.args.onContentChange();
		});

		// when opening category with just 1 item, autoselect that item
		const openCategory = this.shownCategories.find((item) => item.id === categoryId);
		if (openCategory?.items.length === 1) {
			this.selectItem(openCategory.items[0]!, true);
		}
	}

	@action
	selectItem(item: Item, skipClose = false) {
		this.selectedItem = item;
		this.args.onSelectItem(item, skipClose);
	}
	<template>
		<div
			class="categories-ct {{unless @showAllTime 'hide-all-time'}}"
			{{didInsert @onContentChange}}
		>
			<ul class="categories">
				{{#each this.shownCategories as |category|}}
					<FormRangeCategoryBaseComponent
						@model={{category}}
						@itemComponent={{this.componentClass}}
						@hideUnsupported={{@hideUnsupported}}
						@theme={{this.theme}}
						@openCategoryId={{this.openCategoryId}}
						@selectedItem={{this.selectedItem}}
						@toggleCategory={{this.toggleCategory}}
						@selectItem={{this.selectItem}}
						@customPeriodPickerOptions={{@customPeriodPickerOptions}}
					/>
				{{else}}
					<p class="empty">No entries available.</p>
				{{/each}}
			</ul>
		</div>
	</template>
}
